import './Editor.css'

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Brush24, Component24, Dialog24, Menu24, Screen24, Setting24 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Sidebar, Tabs, useTheme } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { LeadingSidebarTab } from '@/shared/builder-ui'

import { ComponentPanel } from './ComponentPanel'
import { DialogPanel } from './DialogPanel'
import { MenuPanel } from './MenuPanel'
import { PagePanel } from './PagePanel'
import { SettingPanel } from './SettingPanel'
import { StylePanel } from './StylePanel'

interface BuilderTab {
  id: LeadingSidebarTab
  title: string
  subtitle: string
  icon: React.ReactNode
  component: React.ReactNode
}

export default function BuilderEditor() {
  const { t } = useTranslation()
  const { activeTab, setActiveTab, activeComponent, setActiveComponent } = useAppBuilderContext()
  const { pages, activePage } = useAppPagesContext()
  const { state: themeState } = useTheme()

  const isDarkMode = useMemo(() => {
    return (
      themeState.mode === 'dark' ||
      ((themeState as any).mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    )
  }, [themeState])

  const getTabIconColor = (tabId: LeadingSidebarTab) => {
    if (isDarkMode) {
      switch (tabId) {
        case LeadingSidebarTab.Page:
          return 'text-[#5B9BD5]'
        case LeadingSidebarTab.Component:
          return 'text-[#e1c09c]'
        case LeadingSidebarTab.Style:
          return 'text-[#b6d477]'
        case LeadingSidebarTab.Menu:
          return 'text-[#bcc5f1]'
        case LeadingSidebarTab.Dialog:
          return 'text-[#FFB6C1]'
        case LeadingSidebarTab.Setting:
          return 'text-[#a8abb3]'
        default:
          return 'text-gray-500'
      }
    } else {
      switch (tabId) {
        case LeadingSidebarTab.Page:
          return 'text-[#1e40af]'
        case LeadingSidebarTab.Component:
          return 'text-[#d97706]'
        case LeadingSidebarTab.Style:
          return 'text-[#16a34a]'
        case LeadingSidebarTab.Menu:
          return 'text-[#7c3aed]'
        case LeadingSidebarTab.Dialog:
          return 'text-[#dc2626]'
        case LeadingSidebarTab.Setting:
          return 'text-[#6b7280]'
        default:
          return 'text-gray-600'
      }
    }
  }

  const EDITOR_TABS: BuilderTab[] = [
    {
      id: LeadingSidebarTab.Page,
      title: 'Pages',
      subtitle: 'Screens',
      icon: <Screen24 className={getTabIconColor(LeadingSidebarTab.Page)} />,
      component: <PagePanel />,
    },
    {
      id: LeadingSidebarTab.Component,
      title: 'Components',
      subtitle: 'Components',
      icon: <MaterialIcon icon='add_circle' size={24} className={getTabIconColor(LeadingSidebarTab.Component)} />,
      component: <ComponentPanel />,
    },
    {
      id: LeadingSidebarTab.Style,
      title: 'Style',
      subtitle: 'Style',
      icon: <MaterialIcon icon='palette' size={24} className={getTabIconColor(LeadingSidebarTab.Style)} />,
      component: <StylePanel />,
    },
    {
      id: LeadingSidebarTab.Menu,
      title: 'Menus',
      subtitle: 'Menus',
      icon: <MaterialIcon icon='menu' size={24} className={getTabIconColor(LeadingSidebarTab.Menu)} />,
      component: <MenuPanel />,
    },
    {
      id: LeadingSidebarTab.Dialog,
      title: 'Dialogs',
      subtitle: 'Dialogs',
      icon: <MaterialIcon icon='call_to_action' size={24} className={getTabIconColor(LeadingSidebarTab.Dialog)} />,
      component: <DialogPanel />,
    },
    {
      id: LeadingSidebarTab.Setting,
      title: 'Settings',
      subtitle: 'Settings',
      icon: <MaterialIcon icon='settings' size={24} className={getTabIconColor(LeadingSidebarTab.Setting)} />,
      component: <SettingPanel />,
    },
  ]

  return (
    <Sidebar
      withHandle={false}
      className={cn('h-full! z-10')}
      initialWidth={372}
      resizable
      maxWidth={500}
      minWidth={136}
    >
      <div className='leading-sidebar  h-full w-full bg-white dark:bg-slate-800'>
        <Tabs activeKey={activeTab} className='mk-builder-tab' defaultActiveKey={activeTab}>
          <Tabs.Nav activeKey={activeTab} className='mk-builder-nav-container'>
            <div className='w-full flex items-center justify-between'>
              {EDITOR_TABS.map((tab) => {
                let show = true

                if (tab.id === LeadingSidebarTab.Component) {
                  show = !!activePage && Boolean(pages && pages.length > 0)
                }

                return !show ? null : (
                  <Tabs.NavItem
                    title={t(tab.title)}
                    className={cn(
                      'mk-builder-nav-item !h-auto !py-3',
                      tab.id !== activeTab ? '!bg-[#f4f7f6] dark:!bg-gray-900' : 'dark:!bg-gray-700'
                    )}
                    eventKey={tab.id}
                    key={tab.id}
                    id={tab.id}
                    onClick={() => {
                      if (tab.id === LeadingSidebarTab.Component && activeComponent) {
                        setActiveComponent(undefined)
                      }
                      setActiveTab(tab.id)
                    }}
                  >
                    <div className='flex flex-col items-center justify-center gap-0'>
                      {tab.icon}
                      {tab.subtitle && (
                        <span className='text-[8px] text-gray-500 dark:text-gray-200 uppercase leading-loose'>
                          {t(tab.subtitle)}
                        </span>
                      )}
                    </div>
                  </Tabs.NavItem>
                )
              })}
            </div>
          </Tabs.Nav>

          <div className='mk-builder-panels'>
            {EDITOR_TABS.map((tab) => {
              return (
                <Tabs.Panel className='h-full' noPadding unmountOnExit mountOnEnter key={tab.id} eventKey={tab.id}>
                  {tab.component}
                </Tabs.Panel>
              )
            })}
          </div>
        </Tabs>
      </div>
    </Sidebar>
  )
}
